// src/firebase-client.js
import { initializeApp, getApp, getApps } from 'firebase/app';
import { browserLocalPersistence, getAuth, initializeAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { createPersistentFirestore } from './utils/firestoreOffline';

const firebaseConfig = {
  apiKey:               process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:           process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:            process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:        process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:                process.env.REACT_APP_FIREBASE_APP_ID,
};

// Guarantee we never create two apps (mixing compat elsewhere can silently do this)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const functionsRegion = process.env.REACT_APP_FIREBASE_REGION || 'europe-west1';

// IMPORTANT: auth + functions come from the SAME app + SAME region
const AUTH_PROXY_HOST = firebaseConfig.projectId
  ? `${functionsRegion}-${firebaseConfig.projectId}.cloudfunctions.net/authProxy`
  : '';
const DEFAULT_AUTH_API_HOST = 'identitytoolkit.googleapis.com';
const DEFAULT_AUTH_TOKEN_HOST = 'securetoken.googleapis.com';
const DEFAULT_AUTH_API_SCHEME = 'https';
const AUTH_PROXY_SESSION_KEY = 'memberNest.useAuthProxy';

const auth = (() => {
  try {
    return initializeAuth(app, {
      persistence: browserLocalPersistence,
    });
  } catch (err) {
    return getAuth(app);
  }
})();
const db = createPersistentFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, functionsRegion); // must match your deploy

if (readAuthProxySession()) {
  setAuthProxyEnabled(true);
}

function setAuthProxyEnabled(enabled) {
  if (!AUTH_PROXY_HOST || !auth?.config || !('apiHost' in auth.config) || !('tokenApiHost' in auth.config)) {
    return false;
  }
  auth.config.apiScheme = DEFAULT_AUTH_API_SCHEME;
  auth.config.apiHost = enabled ? AUTH_PROXY_HOST : DEFAULT_AUTH_API_HOST;
  auth.config.tokenApiHost = enabled ? AUTH_PROXY_HOST : DEFAULT_AUTH_TOKEN_HOST;
  return true;
}

function readAuthProxySession() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(AUTH_PROXY_SESSION_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function persistAuthProxySession(enabled) {
  if (typeof window === 'undefined') return;
  try {
    if (enabled) {
      window.sessionStorage.setItem(AUTH_PROXY_SESSION_KEY, '1');
    } else {
      window.sessionStorage.removeItem(AUTH_PROXY_SESSION_KEY);
    }
  } catch (err) {}
}

// If you use emulators anywhere, ensure it's only in localhost guards and consistent
// import { connectFunctionsEmulator } from 'firebase/functions';
// if (window?.location?.hostname === 'localhost') connectFunctionsEmulator(functions, '127.0.0.1', 5001);

export { app, auth, db, storage, functions, persistAuthProxySession, setAuthProxyEnabled };
